import Cart from "cart-store";
import { formatMoney } from "utils";

/**
 * <tier-progress-bar>
 * Visual-only multi-tier cart reward progress bar.
 * Reads cart total (cents) and a JSON list of tiers, then live-updates
 * by subscribing to the theme's cart store (same source the free
 * shipping bar uses). Does NOT add anything to the cart.
 */
class TierProgressBar extends HTMLElement {
  connectedCallback() {
    this.fill = this.querySelector(".tier-bar__fill");
    this.nodes = Array.from(this.querySelectorAll(".tier-bar__node"));
    this.messageEl = this.querySelector(".tier-bar__message-text");

    try {
      this.tiers = JSON.parse(this.getAttribute("tiers") || "[]");
    } catch (e) {
      this.tiers = [];
    }
    this.tiers.sort((a, b) => a.threshold - b.threshold);
    this.maxThreshold = this.tiers.length
      ? this.tiers[this.tiers.length - 1].threshold
      : 0;

    this.value = Number(this.getAttribute("value")) || 0;

    // Fallback for the template-clone path (drawer rebuilt from <template>):
    // read the cart JSON the theme prints in <head> so we show the right
    // state before the store pushes its first update.
    if (!this.value) {
      const data = document.getElementById("cart-data");
      if (data) {
        try {
          this.value = JSON.parse(data.textContent).total_price || 0;
        } catch (e) {
          /* ignore */
        }
      }
    }

    this.update();

    if (Cart && typeof Cart.subscribe === "function") {
      this.unsubscribe = Cart.subscribe((state) => {
        const total = state && state.cart ? state.cart.total_price : null;
        if (total != null && total !== this.value) {
          this.value = total;
          this.update();
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  update() {
    if (!this.maxThreshold) return;

    const pct = Math.min(100, Math.round((this.value / this.maxThreshold) * 100));
    if (this.fill) this.fill.style.width = pct + "%";

    this.nodes.forEach((node) => {
      const threshold = Number(node.getAttribute("data-threshold"));
      node.classList.toggle("is-unlocked", this.value >= threshold);
    });

    if (!this.messageEl) return;

    const next = this.tiers.find((t) => this.value < t.threshold);
    if (next) {
      const left = next.threshold - this.value;
      const money = formatMoney(left, window.theme.money_format).replace(/[.,]00$/, "");
      const label = next.label || "your next reward";
      this.messageEl.innerHTML =
        "You're <strong>" + money + "</strong> away from " + label;
    } else {
      this.messageEl.innerHTML = "You've unlocked every free gift \u2014 nice!";
    }
  }
}

if (!customElements.get("tier-progress-bar")) {
  customElements.define("tier-progress-bar", TierProgressBar);
}
